import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { TimezoneService, TZone } from '../../services/time-zone.service';
import { DEFAULT_APP_CULTURE_CODE, Language } from '../../languages/language.model';
import produce from 'immer';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import moment from 'moment-timezone';
import * as pwValidator from './password-validator';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Component({
  selector: 'topo-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  standalone: false
})
export class UserProfileComponent implements OnInit, OnDestroy {
  @ViewChild('photo') photo;
  @Input() customPlaceholderText = 'Choose...';
  @Input() customNotFoundText = 'No zone found';
  @Input() clearable = false;
  @Input() virtualScroll = true;
  @Input() disabled = false;
  passwordForm: FormGroup;
  imageUrl: string;
  userProfile: fromUserProfile.UserProfile;
  profileForm: FormGroup;
  private $destroy = new Subject<void>();
  isActive$: Observable<boolean>;
  languages: Language[];
  isReady = false;
  changePassword = false;
  showPasswordFields = false;
  public errorMessage$: Observable<string>;
  timezones: TZone[];
  supportedUnits = ['MILES', 'KILOMETERS'];
  profileLabel: string;
  profileLabelColor: string;
  colors = ['#4782B6', '#FBE7E8', '#FFBA08', '#3A8E06', '#EE6400', '#6747E9'];
  croppedImage = '';
  showCropper = false;
  isEditingPhoto = false;
  imageFile: any = null;

  constructor(
    private store: Store<fromUserProfile.State>,
    private fb: FormBuilder,
    private notify: SystemNotificationsService,
    private translateService: TranslateService,
    private timezoneService: TimezoneService
  ) {}

  ngOnDestroy(): void {
    this.$destroy.next();
    this.$destroy.complete();
  }

  ngOnInit(): void {
    this.errorMessage$ = this.store.select(fromUserProfile.selectError);

    this.timezones = this.timezoneService.getTimezones();

    this.store.dispatch(fromUserProfile.showProfileForm({ showProfileForm: false }));
    this.store.dispatch(fromUserProfile.showPasswordForm({ showPasswordForm: false }));
    this.store
      .select(fromUserProfile.selectShowPasswordForm)
      .pipe(takeUntil(this.$destroy))
      .subscribe(showPass => (this.showPasswordFields = showPass));

    this.store
      .select(fromUserProfile.selectShowProfileForm)
      .pipe(
        tap(isActive => {
          isActive ? this.initForm() : this.clearForm();
        }),
        filter(isActive => isActive),
        switchMap(() => this.store.select(fromUserProfile.selectProfileUser)),
        tap(userProfile => (this.userProfile = userProfile)),
        takeUntil(this.$destroy)
      )
      .subscribe(userProfile => {
        let zone = userProfile?.culture?.timezone;
        if (!zone || zone.length === 0 || !moment.tz(zone).tz()) {
          zone = moment.tz.guess();
        }

        let unit = userProfile?.culture?.distanceUnit;
        if (!unit || unit.length === 0) {
          unit = 'miles';
        }

        this.profileForm.patchValue({
          ...userProfile,
          distanceUnit: unit.toUpperCase(),
          timezone: this.timezones.find(tz => tz?.nameValue?.includes(zone)),
          cultureCode: userProfile?.culture?.cultureCode,
          use24HTime: userProfile?.culture?.use24HTime === true,
          phoneNumber: userProfile?.phoneNumber
        });

        this.profileLabel = this.getInitials();
        this.profileLabelColor = this.getBackgroundColor(this.profileLabel);
      });

    this.store
      .select(fromUserProfile.selectProfileConnection)
      .pipe(takeUntil(this.$destroy))
      .subscribe(connectionType => {
        this.changePassword = this.showPasswordForm(connectionType);
      });

    this.store
      .select(fromUserProfile.selectUserLanguages)
      .pipe(takeUntil(this.$destroy))
      .subscribe(languages => {
        this.languages = languages;
      });

    this.errorMessage$
      .pipe(
        filter(err => !!err),
        takeUntil(this.$destroy),
        tap(value => {
          value = `ERRORS.${value}`;
          this.translateService.get(value).subscribe(res => {
            this.notify.error(res);
          });
        })
      )
      .subscribe();
  }

  changePhoto(): void {
    this.photo.nativeElement.click();
  }

  fileChangeEvent(event: any): void {
    const file = event.target.files[0];
    this.imageFile = file;
    this.isEditingPhoto = true;
  }

  imageCropped(event: ImageCroppedEvent): void {
    this.croppedImage = event.base64;
  }

  imageLoaded(): void {
    this.showCropper = true;
  }

  cropperReady(): void {}

  loadImageFailed(): void {}

  clearPhoto(): void {
    this.profileForm.patchValue({ profilePhotoUrl: null });
  }

  closeImageCropper(): void {
    this.showCropper = false;
    this.isEditingPhoto = false;
    this.imageFile = null;
    this.croppedImage = null;
  }

  updatePhoto(): void {
    this.profileForm.patchValue({ profilePhotoUrl: this.croppedImage });

    this.closeImageCropper();
  }

  openNotificationPrefs(): void {
    this.store.dispatch(fromUserProfile.showNotificationPreferences());
  }

  onSubmit(form: FormGroup, close: boolean): void {
    if (!form.valid) {
      return;
    }

    const userProfile = produce(this.userProfile, draftState => {
      draftState.displayName = form.value.displayName?.trim();
      draftState.fullName = form.value.fullName?.trim();
      if (!!!draftState.culture) {
        draftState.culture = {};
      }
      draftState.culture.distanceUnit = form.value.distanceUnit.toLowerCase();
      draftState.culture.timezone = form.value.timezone.nameValue;
      draftState.culture.cultureCode = form.value.cultureCode;
      draftState.culture.use24HTime = form.value.use24HTime;
      draftState.profilePhotoUrl = form.value.profilePhotoUrl;
      draftState.phoneNumber = form.value.phoneNumber;
    });

    if (!!!userProfile.culture.cultureCode) {
      if (this.languages.length) {
        userProfile.culture.cultureCode = this.languages[0].cultureCode;
      } else {
        userProfile.culture.cultureCode = DEFAULT_APP_CULTURE_CODE;
      }
    }

    this.store.dispatch(fromUserProfile.saveUserProfile({ userProfile, close: close }));
  }

  showPasswordForm(connectionType: string): boolean {
    if (connectionType) {
      return connectionType.toUpperCase === 'Username-Password-Authentication'.toUpperCase ? true : false;
    }
    return false;
  }

  onChangePassword(): void {
    this.store.dispatch(fromUserProfile.showPasswordForm({ showPasswordForm: true }));
  }

  onClose(): void {
    this.cancelClick();
  }

  onBack(): void {
    this.store.dispatch(fromUserProfile.showPasswordForm({ showPasswordForm: false }));
  }

  cancelClick(): void {
    this.store.dispatch(fromUserProfile.showPasswordForm({ showPasswordForm: false }));
    this.store.dispatch(fromUserProfile.showProfileForm({ showProfileForm: false }));
  }

  onPwSubmit(form: FormGroup): void {
    if (!form.valid) {
      return;
    }

    this.store.dispatch(fromUserProfile.changePassword({ oldPass: form.value['currentPassword'], newPass: form.value['newPassword'] }));
  }

  private initForm(): void {
    this.profileForm = this.fb.group({
      fullName: [null, Validators.required],
      displayName: [null, Validators.required],
      emailAddress: [null, [Validators.required, Validators.email]],
      distanceUnit: [null, Validators.required],
      cultureCode: [null, Validators.required],
      timezone: [null, Validators.required],
      profilePhotoUrl: [null],
      use24HTime: [null],
      phoneNumber: null
    });

    this.passwordForm = this.fb.group(
      {
        currentPassword: [null, Validators.required],
        newPassword: [
          null,
          [Validators.required, Validators.minLength(8), Validators.nullValidator, pwValidator.PasswordStrength, pwValidator.SpecialChar]
        ],
        passwordConfirm: [null, Validators.required]
      },
      { validators: [pwValidator.matchPasswordValidator, pwValidator.reuseValidator] }
    );

    this.isReady = true;
  }

  private clearForm(): void {
    // await animation
    setTimeout(() => {
      this.isReady = false;
      if (!!this.profileForm) {
        this.profileForm.reset();
      }
    }, 200);
  }

  getInitials(): string {
    let value = 'AA';

    const name = this.profileForm.get('fullName').value;

    if (!!name) {
      const segments = name.split(' ');
      if (segments.length > 1) {
        value = `${segments[0][0]}${segments[1][0]}`;
      } else {
        value = name.length > 1 ? name.substring(0, 2) : name;
      }
    }

    value = value.toLocaleUpperCase();

    return value;
  }

  getBackgroundColor(initials: string): string {
    if (initials) {
      const code = initials.charCodeAt(0);
      const index = code % 6;

      return this.colors[index];
    }

    return this.colors[0];
  }

  getLanguageDisplay(lang: Language) {
    if (!!lang) {
      return this.translateService.instant(`LANGUAGES.NATIVE.LANGUAGE-${lang.cultureCode}`);
    }
    return '';
  }
}
